import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import type { AccessSettings } from '../manage-access/manage-access.service';
import type {
  InputMapping,
  InputMappingDetectionSources,
  InputMappingField,
} from '../input-mapping';
import type { UserSummary } from '../users/user-summary';
import type {
  JobProgressDetail,
  JobResponse,
  JobStatus,
  JobType,
} from '../jobs/jobs-api.service';

export type { JobResponse } from '../jobs/jobs-api.service';

export type ProjectAccessRole = 'owner' | 'member' | 'everyone';

export interface ProjectPermissions {
  canRead: boolean;
  canEdit: boolean;
  canManageAccess: boolean;
}

export interface SharingMemberSummary {
  id: string;
  displayName: string;
}

export interface ProjectSharingSummary {
  level: 'invite-only' | 'everyone';
  memberCount: number;
  memberPreview: SharingMemberSummary[];
}

export interface CreateProjectRequest {
  name: string;
  notes?: string;
  sbmProjectNumber?: string;
  access: Pick<AccessSettings, 'level' | 'memberUserIds'>;
}

export interface JobSummary {
  id: string;
  jobType?: JobType;
  status: JobStatus;
  progress: string | null;
  schemaVersion: 1;
  revision: number;
  progressDetail: JobProgressDetail | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
}

export interface ProjectResponse {
  id: string;
  name: string;
  status: string;
  notes?: string;
  sbmProjectNumber?: string | null;
  fileName?: string | null;
  fileSize?: number | null;
  rowCount?: number | null;
  createdAt: string;
  updatedAt?: string;
  owner: UserSummary;
  accessRole: ProjectAccessRole;
  permissions: ProjectPermissions;
  sharing?: ProjectSharingSummary;
  jobCount?: number;
  latestJob?: JobSummary;
}

export interface UpdateProjectRequest {
  name?: string;
  notes?: string;
  sbmProjectNumber?: string | null;
  status?: 'draft' | 'mapped' | 'ready' | 'processing' | 'completed' | 'failed';
}

export interface ProjectDetailResponse extends ProjectResponse {
  fileS3Key?: string | null;
  detectedColumns?: string[];
  detectedMapping?: InputMapping;
  activeMapping?: InputMapping;
  detectionSources?: InputMappingDetectionSources;
  preview?: Record<string, unknown>[];
  fileType?: 'excel' | 'csv' | 'parquet' | 'feather';
  sheetNames?: string[];
  detectedSheet?: string | null;
  detectedHeaderRow?: number | null;
  columnMappings?: Array<{
    logicalField: InputMappingField;
    sourceColumn: string;
    confidence?: number;
  }>;
}

export interface ProjectsListResponse {
  projects: ProjectResponse[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

@Injectable({ providedIn: 'root' })
export class ProjectsApiService {
  private readonly http = inject(HttpClient);
  private readonly base = '/api/projects';
  private readonly collectionUrl = `${this.base}/`;

  createProject(request: CreateProjectRequest): Observable<ProjectResponse> {
    return this.http.post<ProjectResponse>(this.collectionUrl, request);
  }

  getProject(projectId: string): Observable<ProjectDetailResponse> {
    return this.http.get<ProjectDetailResponse>(`${this.base}/${encodeURIComponent(projectId)}`);
  }

  updateProject(
    projectId: string,
    request: UpdateProjectRequest,
  ): Observable<ProjectDetailResponse> {
    return this.http.patch<ProjectDetailResponse>(
      `${this.base}/${encodeURIComponent(projectId)}`,
      request,
    );
  }

  deleteProject(projectId: string): Observable<{ success: boolean; message: string }> {
    return this.http.delete<{ success: boolean; message: string }>(
      `${this.base}/${encodeURIComponent(projectId)}`,
    );
  }

  listProjects(options?: {
    page?: number;
    limit?: number;
    status?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    search?: string;
  }): Observable<ProjectsListResponse> {
    const params: Record<string, string | number> = {};
    if (options?.page) params['page'] = options.page;
    if (options?.limit) params['limit'] = options.limit;
    if (options?.status) params['status'] = options.status;
    if (options?.sortBy) params['sortBy'] = options.sortBy;
    if (options?.sortOrder) params['sortOrder'] = options.sortOrder;
    if (options?.search) params['search'] = options.search;

    return this.http.get<ProjectsListResponse>(this.collectionUrl, { params });
  }

  /**
   * Start validation job for a project.
   */
  startValidation(
    projectId: string,
    options: Record<string, unknown> = {},
  ): Observable<JobResponse> {
    const encodedProjectId = encodeURIComponent(projectId);
    return this.http.post<JobResponse>(
      `${this.base}/${encodedProjectId}/validations`,
      { options },
    );
  }
}
